/* eslint max-classes-per-file: ["error", 2] */

'use strict';

define('vb/private/stateManagement/rules/ruleSet',[
  'vb/private/log',
  'vb/private/constants',
  'vb/private/stateManagement/rules/rulesUtils',
  'vb/private/stateManagement/rules/basicRule',
  'vb/private/stateManagement/rules/defaultValueRule'],
(Log, Constants, RulesUtils, BasicRule, DefaultValueRule) => {
  const logger = Log.getLogger('/vb/stateManagement/rules');
  const DEFAULT_RULE_ID_PREFIX = '__vb_defaultValue_';

  /**
   * A thin wrapper over the RuleSet class that only has a limited set of rules it runs
   */
  class LiveRuleSet {
    constructor(ruleSet, rules) {
      this.wrapped = ruleSet;
      this.rules = rules;
      this.targetExprs = ruleSet.targetExprs;
      this.targetConstants = ruleSet.targetConstants;
      this.rulesEngine = ruleSet.rulesEngine;
    }

    run(contextChange, liveRulesSet) {
      return this.wrapped.run.call(this, contextChange, liveRulesSet);
    }

    haveValuesForAllTargets(results) {
      return this.wrapped.haveValuesForAllTargets.call(this, results);
    }
  }

  /**
   *  A ruleSet contains the rules defined for a container instance (either base or its extensions) and a ruleSet
   *  shares a rulesContext across all rules. It is usually a subset of the availableContexts available to the
   *  container - namely $constants, $variables, $base, $modules. The first three prevent writebacks and
   *  $modules are modules whose functions cannot return a Promise.
   *
   *  The execution of the RuleSet runs each rule, after it is determined that the rule can execute, based on the
   *  condition set and referencedContext properties.
   */
  class RuleSet {
    constructor(rulesEngine, extensionContainer, rulesDef = []) {
      const getConstantsFromTargetExprs = (targetExprs = []) => {
        const targetConstants = [];
        targetExprs.forEach((te) => {
          const c = RulesUtils.constantNameFromExpr(te);
          if (!targetConstants.includes(c)) {
            targetConstants.push(c);
          }
        });
        return targetConstants;
      };

      this.log = logger;
      this.rulesEngine = rulesEngine;
      this.extensionContainer = extensionContainer;
      this.targetExprs = rulesEngine.rulesConstantsListByExt[this.extensionContainer.extensionId];
      this.targetConstants = getConstantsFromTargetExprs(this.targetExprs);
      this.rules = rulesDef.map((ruleDef) => RuleSet.createRule(ruleDef, extensionContainer, this.targetExprs));
    }

    dispose() {
      this.extensionContainer = null;
      this.rules.forEach((r) => r.dispose());
    }

    /**
     * Check if we have values for all target constants filled by this ruleset. We check for two things - one that
     * the constant expression used is valid and the target constant whose value is provided matches the list of
     * target constants.
     * @param results
     * @returns {boolean} true if every value provided by ruleset is an allowed constant
     */
    haveValuesForAllTargets(results) {
      const resKeys = Object.keys(results) || [];
      // we must have filled all target constants, and the results must match the expected constants.
      return (this.targetConstants.length === resKeys.length) && resKeys.length > 0 && resKeys.every((rkeyExpr) => {
        const c = RulesUtils.constantNameFromExpr(rkeyExpr);
        return this.targetExprs.includes(rkeyExpr) && this.targetConstants.includes(c);
      });
    }

    /**
     * Creates a set of default rules that contains the defaultValue expression along with the proper contexts for
     * evaluating the same.
     * @param rulesEngine
     * @param constantsDef
     * @returns {Object}
     */
    static createDefaultRules(rulesEngine, constantsDef = {}) {
      const defaultRules = {};
      Object.keys(constantsDef).forEach((icName) => {
        const rid = `${DEFAULT_RULE_ID_PREFIX}${icName}`;
        const { rulesScopes, dependencies } = constantsDef[icName];
        // if anything other than the topmost rules scope has valueFrom: inline use it
        const dvRS = rulesScopes.find((rs, index) => (rulesScopes[0].valueFrom
          === Constants.VariablePropertyValueFrom.RULES)
          && (index > 0 && rs.valueFrom === Constants.VariablePropertyValueFrom.INLINE));

        if (dvRS) {
          // of the form { "$base.constants.foo": defaultValue }
          const { extension } = dvRS;
          // there can be multiple ways to express a constant, so pick one
          const expr = RulesUtils.exprsFromConstantName(extension, icName);
          const singularExpr = Array.isArray(expr) ? expr[0] : expr;
          const defaultValue = { [singularExpr]: dvRS.defaultValue };
          // Keep the raw defaultValue so `$withEnv` runs can rebuild expression defaults with the current rules proxy.
          const defaultValueDefinition = { [singularExpr]: constantsDef[icName].defaultValue };

          defaultRules[icName] = RuleSet.createRule({
            id: rid,
            defaultValue,
            defaultValueDefinition,
            dependencies,
          }, extension, [singularExpr]);
        } else {
          logger.finer('The constant', icName, 'does not appear to have a defaultValue configured in either the base',
            'container', rulesEngine.container.id, 'or any of its extensions. So a DefaultValueRule is not created.');
        }
      });
      return defaultRules;
    }

    /**
     * Creates a rule setting up expressions to properly evaluate condition and overlay
     * @param {Object} ruleDef
     * @param {Object} container for the current rule
     * @param {Array} targetConstants that can be wired in rule
     * @returns {BasicRule}
     */
    static createRule(ruleDef, container, targetConstants) {
      if (ruleDef.defaultValue) {
        return new DefaultValueRule(ruleDef, container, targetConstants);
      }
      return new BasicRule(ruleDef, container, targetConstants);
    }

    /**
     * Called right before rules are run, this runs the rules in reverse order (low to high) gathering configured
     * values of constants by executing rules whose condition evaluates to true. This works similar to defaultValue
     * between base and customer extensions. The highest configured value for constants is tracked along with the
     * rules. This call is followed by run()
     * @param {Object|undefined} contextChange change payload describing the
     *  variable/constant that triggered re-evaluation
     * @param {Object} liveRulesState with props ruleSet and rulesConstants. This object is provided by caller
     * and is updated in situ
     * @param {Object} liveRulesState.ruleSet live set of rules to run
     * @param {Object} [liveRulesState.externalContexts] optional temporary contexts for condition setup
     * @return {Object} map of constants to their final rule value expression
     */
    prepareRun(contextChange, liveRulesState) {
      const { ruleSet: lrs } = liveRulesState;
      const ruleValues = {};
      if (Object.keys(this.targetExprs).length) {
        const liveRules = [];
        const reversed = this.rules.slice().reverse();
        reversed.forEach((lrule) => {
          const overlayConstants = lrule.prepareRun(contextChange, liveRulesState);
          if (typeof overlayConstants === 'object' && Object.keys(overlayConstants).length) {
            // after the pre-execution of each rule store the value of the constant expr in results. If the previous
            // rule returned a value for a constant, overwrite the assignment.
            Object.keys(overlayConstants).forEach((targetConstant) => {
              ruleValues[targetConstant] = overlayConstants[targetConstant];
            });
            liveRules.unshift(lrule);
          }
        });
        if (liveRules.length) {
          lrs.unshift(new LiveRuleSet(this, liveRules));
        }
      }
      return ruleValues;
    }

    /**
     * The result of running a RuleSet is a Map of interface constants to their values.
     * @param {Object|undefined} contextChange event payload containing the rules context change
     * @param {Object} liveRulesState with props ruleSet and rulesConstants. This object is provided by caller
     * and is updated in situ with the intermediate results of the constants rules evaluation
     * @param {Object} liveRulesState.ruleSet live set of rules to run
     * @param {Object} liveRulesState.ruleConstants map of constants with their expressions
     * @param {Object} liveRulesState.ruleValues: map of constants and their rule evaluated values
     */
    run(contextChange, liveRulesState) {
      const results = {};
      // if we have at least one target constant to fill, run rules.
      if (Object.keys(this.targetExprs).length) {
        let continueRun = true;
        let nextIndex;
        for (nextIndex = 0; continueRun && nextIndex < this.rules.length; nextIndex++) {
          const rule = this.rules[nextIndex];
          const retVal = rule.run(contextChange, liveRulesState);
          if (typeof retVal === 'object') {
            // after the execution of each rule store the value of the constant in results. If the previous rule
            // already returned a value for a constant, skip the assignment. If all constants have a value skipping
            // running any more rules in the current ruleset
            Object.keys(retVal).forEach((targetConstant) => {
              // Use loose inequality intentionally: `x != null` is false only for null/undefined, so this preserves
              // valid falsy values without using the ES2020 `??` operator. TODO: Logged
              // https://jira.oraclecorp.com/jira/browse/VBS-50342 to take care of the null value as well, leaving it
              // as is for now until we can coordinate with the DT team.
              results[targetConstant] = (results[targetConstant] != null)
                ? results[targetConstant]
                : retVal[targetConstant];
            });
          }
          continueRun = !this.haveValuesForAllTargets(results);
        }
      }
      return results;
    }
  }

  return RuleSet;
});

